/**
 * CalfERPSettingsContext
 * Provides calf ERP configuration, benchmarks, and thresholds to all components
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useAuth } from "./AuthContext";
import {
	getCalfERPSettings,
	saveCalfERPSettings,
	DEFAULT_CALF_ERP_SETTINGS,
} from "../api/firestore/services/calfERPSettingsService";

const CalfERPSettingsContext = createContext(null);

export function CalfERPSettingsProvider({ children }) {
	const { currentUser } = useAuth();
	const [settings, setSettings] = useState(DEFAULT_CALF_ERP_SETTINGS);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		if (!currentUser?.uid) return;

		let cancelled = false;
		setLoading(true);

		getCalfERPSettings(currentUser.uid)
			.then((data) => {
				if (!cancelled) setSettings(data);
			})
			.catch((err) => {
				console.error("[CalfERPSettings] Error loading settings:", err);
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});

		return () => { cancelled = true; };
	}, [currentUser?.uid]);

	const updateSettings = useCallback(
		async (updates) => {
			if (!currentUser?.uid) return;
			await saveCalfERPSettings(currentUser.uid, updates);
			setSettings((prev) => ({ ...prev, ...updates }));
		},
		[currentUser?.uid]
	);

	const value = {
		settings,
		loading,
		updateSettings,
	};

	return (
		<CalfERPSettingsContext.Provider value={value}>
			{children}
		</CalfERPSettingsContext.Provider>
	);
}

export function useCalfERPSettings() {
	const context = useContext(CalfERPSettingsContext);
	if (!context) {
		// Return defaults if used outside provider (graceful degradation)
		return { settings: DEFAULT_CALF_ERP_SETTINGS, loading: false, updateSettings: async () => {} };
	}
	return context;
}

export default CalfERPSettingsContext;
